import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { useLanguage } from '../contexts/LanguageContext'

const Footer = () => {
  const { t, getLangPrefix } = useLanguage()
  const langPrefix = getLangPrefix()
  const footerLinks = {
    product: [
      { href: '#features', label: t.nav.features },
      { href: '#how-it-works', label: t.nav.howItWorks },
      { href: '#pricing', label: t.nav.pricing },
    ],
    company: [
      { href: '#', label: t.footer.about },
      { href: '#', label: t.footer.blog },
      { href: '#', label: t.footer.career },
    ],
    support: [
      { href: `${langPrefix}/privacy`, label: t.footer.privacy },
      { href: `${langPrefix}/terms`, label: t.footer.termsOfUse },
    ],
  }

  const scrollToSection = (e, href) => {
    if (href.startsWith('#')) {
      e.preventDefault()
      const element = document.querySelector(href)
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }
  }

  return (
    <footer className="py-8 md:py-12 lg:py-16 bg-gradient-to-b from-gray-900 to-black text-white relative overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 lg:gap-12 mb-6 md:mb-8 lg:mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Link to="/" className="flex items-center gap-2 md:gap-3 mb-2 md:mb-4">
              <motion.div
                whileHover={{ rotate: 360 }}
                transition={{ duration: 0.6 }}
                className="w-8 h-8 md:w-10 md:h-10"
              >
                <svg viewBox="0 0 32 32" fill="none" className="w-full h-full">
                  <circle cx="16" cy="16" r="14" fill="url(#logo-gradient-footer)" />
                  <path
                    d="M16 8C16 8 12 12 12 16C12 18.2091 13.7909 20 16 20C18.2091 20 20 18.2091 20 16C20 12 16 8 16 8Z"
                    fill="white"
                  />
                  <circle cx="16" cy="22" r="1.5" fill="white" />
                  <defs>
                    <linearGradient
                      id="logo-gradient-footer"
                      x1="2"
                      y1="2"
                      x2="30"
                      y2="30"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop stopColor="#10b981" />
                      <stop offset="1" stopColor="#059669" />
                    </linearGradient>
                  </defs>
                </svg>
              </motion.div>
              <span className="text-lg md:text-xl font-bold bg-gradient-to-r from-white to-primary-light bg-clip-text text-transparent">
                Juvenis
              </span>
            </Link>
            <p className="text-gray-400 text-xs md:text-sm">
              {t.footer.tagline}
            </p>
          </motion.div>

          {Object.entries(footerLinks).map(([category, links], categoryIndex) => (
            <motion.div
              key={category}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: categoryIndex * 0.1 }}
            >
              <h4 className="font-semibold mb-2 md:mb-3 lg:mb-4 text-sm md:text-base text-primary-light capitalize">
                {category === 'product'
                  ? t.footer.product
                  : category === 'company'
                  ? t.footer.company
                  : t.footer.support}
              </h4>
              <ul className="space-y-1.5 md:space-y-2 lg:space-y-3">
                {links.map((link, index) => {
                  const isInternalLink = link.href.startsWith('/')
                  const Component = isInternalLink ? Link : motion.a
                  const props = isInternalLink
                    ? {
                        to: link.href,
                        className: 'text-gray-400 hover:text-white transition-colors text-xs md:text-sm block',
                      }
                    : {
                        href: link.href,
                        onClick: (e) => scrollToSection(e, link.href),
                        whileHover: { x: 4, color: '#34d399' },
                        className: 'text-gray-400 hover:text-white transition-colors text-xs md:text-sm block',
                      }
                  
                  return (
                    <li key={index}>
                      {isInternalLink ? (
                        <motion.div whileHover={{ x: 4 }}>
                          <Component {...props}>{link.label}</Component>
                        </motion.div>
                      ) : (
                        <Component {...props}>{link.label}</Component>
                      )}
                    </li>
                  )
                })}
              </ul>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="pt-4 md:pt-6 lg:pt-8 border-t border-gray-800 text-center text-gray-400 text-xs md:text-sm"
        >
          <p>{t.footer.copyright}</p>
        </motion.div>
      </div>
    </footer>
  )
}

export default Footer
